import { useCallback, useEffect, useRef } from "react"
import { useEventListener } from "ahooks"
import classNames from "classnames"

import {
    inlineVideo,
    isHonorBrowser,
    isMiUIBrowser,
    isOppoBrowser,
    isQuarkBrowser,
    isVivoBrowser,
} from "@/utils/misc"

import styles from "./index.module.less"

interface Props {
    src?: string
    sources?: {
        src: string
        type: string
    }[]
    poster?: string
    renderer?: "canvas" | "video"
    className?: string
    style?: React.CSSProperties
}

const isStaticImage =
    isVivoBrowser() ||
    isMiUIBrowser() ||
    isQuarkBrowser() ||
    isHonorBrowser() ||
    isOppoBrowser()

const VideoPlayer: React.FC<Props> = (props) => {
    const {
        src,
        sources = [],
        poster,
        renderer = "video",
        className,
        style,
    } = props

    const videoRef = useRef<HTMLVideoElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    const initVideo = useCallback(() => {
        const video = videoRef.current || document.createElement("video")
        if (!videoRef.current) {
            videoRef.current = video
        }

        if (sources.length) {
            sources.forEach((source) => {
                const sourceElement = document.createElement("source")
                sourceElement.src = source.src
                sourceElement.type = source.type
                video.appendChild(sourceElement)
            })
        } else if (src) {
            video.src = src
        }

        video.crossOrigin = "anonymous"
        video.preload = "auto"
        video.muted = true
        video.loop = true
        video.playsInline = true
        video.setAttribute("webkit-playsinline", "true")
        video.setAttribute("x5-video-player-type", "h5-page")
        video.setAttribute("x5-video-player-fullscreen", "false")
        video.autoplay = true
        inlineVideo(video)
        // video.play()
    }, [sources, src])

    const draw = useCallback(() => {
        if (!canvasRef.current || !videoRef.current) {
            return
        }

        const ctx = canvasRef.current.getContext("2d")
        if (ctx) {
            ctx.drawImage(
                videoRef.current,
                0,
                0,
                canvasRef.current.width,
                canvasRef.current.height
            )
        }

        requestAnimationFrame(draw)
    }, [])

    const handleResize = useCallback(() => {
        if (!canvasRef.current || !videoRef.current) {
            return
        }
        canvasRef.current.width = canvasRef.current.clientWidth
        canvasRef.current.height = canvasRef.current.clientHeight
        videoRef.current.width = canvasRef.current.width
        videoRef.current.height = canvasRef.current.height
    }, [])

    useEffect(() => {
        if (isStaticImage) {
            return
        }

        initVideo()
        if (renderer === "canvas") {
            draw()
        }

        handleResize()
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [])

    useEventListener(
        ["mousedown", "touchstart", "click"],
        () => {
            if (isStaticImage) {
                return
            }

            videoRef.current?.play().catch((e) => {
                console.error(e)
            })
        },
        { target: window }
    )

    if (isStaticImage) {
        return (
            <img
                className={classNames(styles.player, className)}
                style={style}
                src={poster}
            />
        )
    }

    if (renderer === "canvas") {
        return (
            <canvas
                ref={canvasRef}
                className={classNames(styles.player, className)}
                style={style}
            />
        )
    }

    return (
        <video
            ref={videoRef}
            className={classNames(styles.player, className)}
            style={style}
            onClick={() => {
                videoRef.current?.play()
            }}
            poster={poster}
        />
    )
}

export default VideoPlayer
